import type { MouseEvent } from 'react'
import arrowBigDownUrl from '@/assets/arrow-big-down.svg'
import { cn } from '@/lib/cn'

/** Compact display for large vote totals (e.g. 12K). */
export function formatVoteCount(n: number): string {
  if (!Number.isFinite(n)) return '0'
  const abs = Math.abs(Math.trunc(n))
  if (abs >= 1_000_000) {
    const v = abs / 1_000_000
    return `${v >= 10 ? Math.round(v) : Number(v.toFixed(1))}M`
  }
  if (abs >= 1_000) {
    const v = abs / 1_000
    return `${v >= 10 ? Math.round(v) : Number(v.toFixed(1))}K`
  }
  return String(abs)
}

export function ArrowBigUp({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M9 19a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-6a1 1 0 0 1 1-1h3.293a.707.707 0 0 0 .5-1.207l-7.086-7.086a1 1 0 0 0-1.414 0l-7.086 7.086a.707.707 0 0 0 .5 1.207H8a1 1 0 0 1 1 1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Renders `src/assets/arrow-big-down.svg` — img keeps the asset shape at pill size. */
export function ArrowBigDown({
  className,
  active = false,
}: {
  className?: string
  active?: boolean
}) {
  return (
    <img
      src={arrowBigDownUrl}
      alt=""
      aria-hidden
      draggable={false}
      className={cn(
        'shrink-0 object-contain',
        active
          ? '[filter:invert(63%)_sepia(61%)_saturate(4567%)_hue-rotate(325deg)_brightness(102%)_contrast(97%)]'
          : 'opacity-70 brightness-0 dark:opacity-55 dark:[filter:brightness(0)_invert(1)]',
        className,
      )}
    />
  )
}

export type VotePillProps = {
  value: number
  className?: string
  size?: 'sm' | 'md'
  /** Use 1.2K-style formatting when true */
  compact?: boolean
  upActive?: boolean
  downActive?: boolean
  disabled?: boolean
  onUp?: () => void
  onDown?: () => void
  /** When false, shows the pill without button behavior (e.g. idea cards grid). */
  interactive?: boolean
  /** Accessible label for the vote control */
  ariaLabel?: string
  /** Idea card footer — transparent shell, fixed footprint. */
  variant?: 'default' | 'card'
}

/**
 * Pill with up / count / down — matches product reference (dark capsule, outline arrows).
 */
export function VotePill({
  value,
  className,
  size = 'md',
  compact = false,
  upActive = false,
  downActive = false,
  disabled = false,
  onUp,
  onDown,
  interactive = true,
  ariaLabel = 'Votes',
  variant = 'default',
}: VotePillProps) {
  const shown = compact ? formatVoteCount(value) : String(value)
  const icon = size === 'sm' ? 'h-3.5 w-3.5 shrink-0' : 'h-4 w-4 shrink-0'
  const pad = size === 'sm' ? 'px-2 py-1' : 'px-2.5 py-1.5'
  const text =
    size === 'sm'
      ? 'min-w-[1.35rem] text-center text-xs font-semibold tabular-nums text-[var(--foreground)] dark:text-white/88'
      : 'min-w-[1.65rem] text-center text-[12px] font-semibold tabular-nums text-[var(--foreground)] dark:text-white/88 sm:text-[13px]'

  const shell = cn(
    'inline-flex items-center justify-center rounded-full',
    variant === 'card'
      ? 'h-[30px] w-[97.59px] shrink-0 gap-1 border border-black/[0.1] bg-black/[0.03] px-1.5 dark:border-white/[0.08] dark:bg-transparent'
      : cn(
          'gap-2 bg-black/[0.05] ring-1 ring-black/[0.08] dark:bg-[#242424] dark:ring-white/[0.08]',
          pad,
        ),
    className,
  )

  const btnBase =
    'rounded-full p-1 transition enabled:cursor-pointer enabled:hover:bg-black/[0.07] dark:enabled:hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-35'
  const idleIcon = 'text-[var(--foreground-muted)] dark:text-white/55'
  /** Validate-idea bar: up = green when voted, down = red when vote can be removed. */
  const upCls = cn(icon, upActive ? 'text-[#00A63E]' : idleIcon)
  if (!interactive) {
    return (
      <div className={shell} aria-label={`${ariaLabel}: ${shown}`} role="group">
        <ArrowBigUp className={cn(icon, idleIcon)} />
        <span className={text}>{shown}</span>
        <ArrowBigDown className={icon} />
      </div>
    )
  }

  // Always stop propagation when the pill is interactive — VotePill is
  // commonly nested inside a card whose own onClick navigates to the idea
  // detail. Without this, clicking an arrow would both vote AND navigate.
  const handle = (cb?: () => void) => (e: MouseEvent) => {
    e.stopPropagation()
    cb?.()
  }

  return (
    // The buttons individually stop propagation; clicks on the (small) gap
    // between buttons fall through. A wrapper onClick would be the cleanest
    // catch-all but trips jsx-a11y/no-noninteractive-element-interactions
    // on a non-interactive `role="group"`.
    <div className={shell} role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className={btnBase}
        disabled={disabled || !onUp}
        onClick={handle(onUp)}
        aria-label="Upvote"
        aria-pressed={upActive}
      >
        <ArrowBigUp className={upCls} />
      </button>
      <span className={text}>{shown}</span>
      <button
        type="button"
        className={btnBase}
        disabled={disabled || !onDown}
        onClick={handle(onDown)}
        aria-label="Downvote"
        aria-pressed={downActive}
      >
        <ArrowBigDown className={icon} active={downActive} />
      </button>
    </div>
  )
}
